import React from "react";
import { Row, Col } from "react-bootstrap";
import { DashboardData } from "../../hooks/useDashboardData";
import UpdateText from "./UpdateText";
import CountyMap from "./Map/CountyMap";
import Daily from "./DailyMetrics/Daily";
import Rate from "./DailyMetrics/Rate";
import ChartHeader from "../../components/CardHeader";
import Filter from "./Filter/DatePresentFilter";
import {
  COLOR_POSITIVE,
  COLOR_POSITIVE_DARK,
  COLOR_TESTED,
  COLOR_DEATHS,
  POSITIVE_TEST_RATE,
  POSITIVE_TEST_RATE_MOVING,
} from "../../GlobalColors";
import { refAreaCalc } from "../../utils/DataConversion";

interface CtsiProps {
  dashboardData: DashboardData | undefined;
  reportDate: string;
  currentFilter: App.CurrentFilter;
  setCurrentFilter: (filter: App.CurrentFilter) => void;
  dateFilter: [number, number] | undefined;
  setDateFilter: (filter: [number, number] | undefined) => void;
  outliers: Data.Outlier[] | undefined;
  metricMapDate: string;
  resetFilter: () => void;
}

export default function Ctsi(props: CtsiProps) {
  const {
    dashboardData,
    reportDate,
    currentFilter,
    dateFilter,
    outliers,
    setCurrentFilter,
    setDateFilter,
    metricMapDate,
    resetFilter,
  } = props;

  return (
    <React.Fragment>
      <UpdateText reportDate={reportDate} />
      <Row>
        <Col className="m-map card-wrapper">
          <div className="map-card card h-100 mb-0">
            {dashboardData ? (
              <CountyMap
                data={dashboardData.map}
                outliers={outliers}
                currentFilter={currentFilter}
                setCurrentFilter={setCurrentFilter}
                metricMapDate={metricMapDate}
                resetFilter={resetFilter}
              />
            ) : null}
          </div>
        </Col>
      </Row>
      <Row>
        {dashboardData ? (
          <div className="m-3">
            <h4>Filter daily charts to the:</h4>
            <Filter setDateFilter={setDateFilter} reportDate={reportDate} />
          </div>
        ) : null}
      </Row>
      <Row>
        <Col md={12} className="m-daily-metrics card-wrapper">
          <div className="m-tabs card mb-0 h-100">
            {dashboardData ? (
              <React.Fragment>
                <ChartHeader
                  title="Positive Cases"
                  tooltipIcon={dateFilter ? "far fa-calendar" : undefined}
                  toolTipText={
                    dateFilter ? "Filtered on Date Range" : undefined
                  }
                />
                <Daily
                  dataKeys={[
                    ["newCases", "newCases"],
                    ["existingCases", "existingCases"],
                    ["caseMoving", "caseMoving"],
                  ]}
                  title={
                    !currentFilter.filters.length
                      ? "Statewide Positive Cases by Day"
                      : currentFilter.displayName + " Positive Cases by Day"
                  }
                  barLabel="Positive Case"
                  data={dashboardData.daily}
                  color={COLOR_POSITIVE}
                  darkColor={COLOR_POSITIVE_DARK}
                />
              </React.Fragment>
            ) : null}
          </div>
        </Col>
        <Col md={12} className="m-daily-metrics card-wrapper">
          <div className="m-tabs card mb-0 h-100">
            {dashboardData && reportDate ? (
              <React.Fragment>
                <ChartHeader
                  title="Tests"
                  tooltipIcon={dateFilter ? "far fa-calendar" : undefined}
                  toolTipText={
                    dateFilter ? "Filtered on Date Range" : undefined
                  }
                />
                <Daily
                  title={
                    !currentFilter.filters.length
                      ? "Statewide Tests by Day"
                      : currentFilter.displayName + " Tests by Day"
                  }
                  dataKeys={[
                    ["newTests", "newTestAdmin"],
                    ["existingTests", "existingTestAdmin"],
                    ["testMoving", "testAdminMoving"],
                  ]}
                  barLabel={"Test"}
                  showIndividualToggle
                  data={dashboardData.daily}
                  color={COLOR_TESTED}
                  startPre={refAreaCalc(reportDate)}
                />
              </React.Fragment>
            ) : null}
          </div>
        </Col>
        <Col md={12} className="m-daily-metrics card-wrapper">
          <div className="m-tabs card mb-0 h-100">
            {dashboardData && reportDate ? (
              <React.Fragment>
                <ChartHeader
                  title="Deaths"
                  tooltipIcon={dateFilter ? "far fa-calendar" : undefined}
                  toolTipText={
                    dateFilter ? "Filtered on Date Range" : undefined
                  }
                />
                <Daily
                  title={
                    !currentFilter.filters.length
                      ? "Statewide Deaths by Day"
                      : currentFilter.displayName + " Deaths by Day"
                  }
                  dataKeys={[
                    ["newDeaths", "newDeaths"],
                    ["existingDeaths", "existingDeaths"],
                    ["deathMoving", "deathMoving"],
                  ]}
                  barLabel="Death"
                  data={dashboardData.daily}
                  color={COLOR_DEATHS}
                />
              </React.Fragment>
            ) : null}
          </div>
        </Col>
        <Col md={12} className="m-daily-metrics card-wrapper">
          <div className="m-tabs card mb-0 h-100">
            {dashboardData && reportDate ? (
              <React.Fragment>
                <ChartHeader
                  title="Positivity"
                  tooltipIcon={dateFilter ? "far fa-calendar" : undefined}
                  toolTipText={
                    dateFilter ? "Filtered on Date Range" : undefined
                  }
                />
                <Rate
                  reportDate={reportDate || ""}
                  currentFilter={currentFilter}
                  dataKeyFormalNames={[
                    "Positive Test Rate",
                    "Positive Rate 7-Day Average",
                  ]}
                  dataKeys={[
                    ["testRate", "testRateMoving"],
                    ["testAdminRate", "testAdminRateMoving"],
                  ]}
                  color={POSITIVE_TEST_RATE}
                  darkColor={POSITIVE_TEST_RATE_MOVING}
                  data={dashboardData.daily}
                  startPre={refAreaCalc(reportDate)}
                  percent
                />
              </React.Fragment>
            ) : null}
          </div>
        </Col>
      </Row>
    </React.Fragment>
  );
}
